/* Include tokens */
import "@spectrum-css/tokens/dist/index.css";
import {
  AlertWhite,
  CallbackIcon,
  CallIcon,
  CasesIcon,
  ChatIcon,
  ContactsIcon,
  CustomResponsesIcon,
  HistoryIcon,
  NotesIcon,
  SearchIcon,
  TemplatesIcon,
  VoicemailIcon,
  WarningIconSidebar,
} from "./../common/Icons";
import "./style.css";
/*
  For components with no other contexts available, load the
  index.css file from the component's package. These are components
  that do not have a spectrum or express context available.
*/
import "@spectrum-css/page/dist/index.css";
import "@spectrum-css/typography/dist/index.css";
import "@spectrum-css/icon/dist/index.css";

/*
  Recommended: For components with multiple contexts available, if you
  want access to all contexts, load the index.css file, which includes
  all contexts and component variables.
*/
import "@spectrum-css/sidenav/dist/index.css";
import { Heading, View, Text } from "@adobe/react-spectrum";
import { setCurrentSessionTab } from "../../actions/session";
import storeRTK from "../../store/storeRTK";
import { useSelector } from "react-redux";
import { RootState } from "../../store/storeConfig";
import {
  AIC_CONNECTING_MESSAGE,
  CASE_NETWORK_ERROR_MSG,
  CHAT_NETWORK_ERROR_MSG,
  VOICE_NETWORK_DISCONNECT,
} from "../../constants/common";
import { useCallback, useMemo } from "react";

const mySpaceItems = [
  { icon: <HistoryIcon color={"#d6d6d6"} />, label: "History" },
  { icon: <ContactsIcon color={"#d6d6d6"} />, label: "Speed Dials" },
  { icon: <SearchIcon color={"#d6d6d6"} />, label: "KB Search" },
  { icon: <TemplatesIcon color={"#d6d6d6"} />, label: "Templates" },
  {
    icon: <CustomResponsesIcon color={"#d6d6d6"} />,
    label: "Custom Responses",
  },
  { icon: <NotesIcon color={"#d6d6d6"} />, label: "Notes" },
];

const CountBadge = ({
  count,
  isSelected,
}: {
  count: number;
  isSelected?: boolean;
}) => (
  <span
    style={{
      color: "#222222",
      borderRadius: "50%",
      fontSize: "0.75rem",
      backgroundColor: isSelected ? "#FFFFFF" : "#E6E6E6",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "20px",
      height: "20px",
      textAlign: "center",
    }}
  >
    {count}
  </span>
);

const ErrorMessage = ({ message }: { message: string }) => (
  <View
    width={"9%"}
    position={"absolute"}
    bottom={"size-0"}
    padding={"8px"}
    backgroundColor={"red-400"}
    borderRadius={"medium"}
    alignSelf={"end"}
    marginBottom={"size-200"}
  >
    <AlertWhite />
    <br />
    <Text UNSAFE_className="vpn_err_msg">{message}</Text>
  </View>
);

const SiderBar = () => {
  const currentTab = useSelector(
    (state: RootState) => state.session.currentTab
  );
  const sessionError = useSelector(
    (state: RootState) => state.session.sessionError
  );
  const ccpSessionState = useSelector(
    (state: RootState) => state.webrtc.ccpSessionState
  );
  const isVoiceNetworkAvailable = useSelector(
    (state: RootState) => state.webrtc.isVoiceNetworkAvailable
  );

  const call = useSelector((state: RootState) => state.call);
  const callback = useSelector((state: RootState) => state.callback);
  const chat = useSelector((state: RootState) => state.chat);
  const voicemail = useSelector((state: RootState) => state.voicemail);
  const cases = useSelector((state: RootState) => state.cases);

  const changeTab = useCallback((id: string) => {
    storeRTK.dispatch(setCurrentSessionTab({ id }));
  }, []);

  const shouldShowWarning = useMemo(() => {
    return (
      ccpSessionState !== 4 || !navigator.onLine || !isVoiceNetworkAvailable
    );
  }, [ccpSessionState, isVoiceNetworkAvailable]);

  const channelItems = useMemo(
    () => [
      {
        key: "call",
        icon: <CallIcon />,
        label: "Call",
        showWarning: true,
        count: call.id ? 1 : 0,
      },
      {
        key: "callback",
        icon: <CallbackIcon />,
        label: "Callback",
        count: callback.id ? 1 : 0,
      },
      {
        key: "voicemail",
        icon: <VoicemailIcon color="#000000" />,
        label: "Voicemail",
        count: voicemail.id ? 1 : 0,
      },
      {
        key: "chat",
        icon: <ChatIcon color="#000000" />,
        label: "Chat",
        count: chat.chats?.length ?? 0,
      },
      {
        key: "case",
        icon: <CasesIcon color="#000000" />,
        label: "Case",
        count: cases.cases?.length ?? 0,
      },
    ],
    [
      call.id,
      callback.id,
      voicemail.id,
      chat.chats?.length,
      cases.cases?.length,
    ]
  );

  return (
    <div
      data-html-preview=""
      className="spectrum spectrum--light spectrum--medium spectrum--express"
      style={{
        margin: "12.6% 12.6% 15% 12.6%",
        backgroundColor: "#F8F8F8",
      }}
    >
      <Heading level={4} UNSAFE_style={{ paddingLeft: "calc(1rem - 1px)" }}>
        Channels
      </Heading>
      <nav>
        <ul
          className="spectrum-SideNav spectrum-SideNav--hasIcon"
          style={{ fontSize: "1rem" }}
        >
          {channelItems.map((item) => (
            <li
              key={item.key}
              className={`sidebar-content spectrum-SideNav-item ${item.key}${
                currentTab === item.key ? " is-selected" : ""
              }`}
              onClick={() => changeTab(item.key)}
            >
              {item.icon}
              <Text UNSAFE_className={`spectrum-SideNav-link-text`}>
                {item.label}
              </Text>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "auto",
                  gap: "4px",
                }}
              >
                {item.showWarning && shouldShowWarning && (
                  <WarningIconSidebar />
                )}
                {item.count > 0 && (
                  <CountBadge
                    count={item.count}
                    isSelected={currentTab === item.key}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      </nav>
      <Heading level={4} UNSAFE_style={{ paddingLeft: "calc(1rem - 1px)" }}>My Space</Heading>
      <nav>
        <ul className="spectrum-SideNav spectrum-SideNav--hasIcon">
          {mySpaceItems.map((item, index) => (
            <li key={index} className="spectrum-SideNav-item sidebar-content">
              {item.icon}
              <Text UNSAFE_className="spectrum-SideNav-link-text disable-text">
                {item.label}
              </Text>
            </li>
          ))}
        </ul>
      </nav>
      {ccpSessionState !== 4 && (
        <ErrorMessage message={AIC_CONNECTING_MESSAGE} />
      )}
      {!navigator.onLine && (
        <ErrorMessage 
          message={
            currentTab === "chat" 
              ? CHAT_NETWORK_ERROR_MSG 
              : currentTab === "case" 
              ? CASE_NETWORK_ERROR_MSG 
              : (sessionError || VOICE_NETWORK_DISCONNECT)
          } 
        />
      )}
      {navigator.onLine && !isVoiceNetworkAvailable && (
        <ErrorMessage message={sessionError || VOICE_NETWORK_DISCONNECT} />
      )}
    </div>
  );
};

export default SiderBar;
